// src/features/leases/components/EditableLeaseField.jsx
//
// LSE6 — read row with a provenance badge that flips into an inline
// edit control on click (admin/editor role only). Save calls
// updateLease() through the existing leasesClient. The server-side
// auto-flip (LSE6 PATCH) sets fieldSources[field] = 'manual'
// automatically — the client does not need to send fieldSources.
//
// Props:
//   - locationId, leaseId       : required for PATCH
//   - lease                     : full lease object (for `before` audit snapshot)
//   - field                     : camelCase field name (e.g. "vendor", "baseYear")
//   - label                     : display label
//   - value                     : current value (raw)
//   - render                    : optional (val) => ReactNode to format the read-mode display
//   - source                    : current provenance ('spacebase'|'manual'|...) or undefined
//   - type                      : 'text' | 'number' | 'date' | 'select' (default 'text')
//   - options                   : [{value,label}] for type='select'
//   - canWrite                  : false → static, no click affordance
//   - suffix                    : right-side trailing label (read mode only, e.g. "sqft")
//   - dateBadge                 : optional <DaysRemainingPill> to render after value
//   - onSaved                   : ({field, value}) => void — caller refreshes
//
// The component is intentionally narrow: it owns ONE field. The Lease
// summary tab composes many of them and the orchestrator handles the
// PATCH-then-refetch lifecycle.

import React, { useState, useRef, useEffect } from "react";
import { T } from "../../../app/tokens.js";
import { DateInput } from "../../../components/DateInput.jsx";
import { ProvenanceTooltip } from "./ProvenanceTooltip.jsx";
import { NotSpecified } from "./NotSpecified.jsx";
import { updateLease } from "../../../data/leasesClient.js";
import { log } from "../../../utils/logger.js";

function coerceForServer(type, raw) {
  if (raw === "" || raw == null) return null;
  if (type === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return raw;
}

export function EditableLeaseField({
  locationId, leaseId, lease,
  field, label, value, source,
  type = "text", options = [],
  canWrite = false,
  render,
  emptyLabel = null,
  suffix,
  dateBadge,
  onSaved,
  autoEdit = false,
  conflictsMap = null,
  onConflictResolve = null,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState("");
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const inputRef              = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  // D4: AccordionFieldPanel's "Expand to fill gaps" CTA auto-focuses the
  // first null field by mounting it with autoEdit=true. The effect runs
  // only at mount ([] dep) — at that point the panel just became visible
  // and autoEdit is already set, so the field enters edit mode immediately.
  // intentional: runs only at mount; autoEdit/canWrite are stable at that point
  useEffect(() => {
    if (autoEdit && canWrite) {
      setDraft(value == null ? "" : String(value));
      setError("");
      setEditing(true);
    }
  }, []);

  function startEdit() {
    if (!canWrite) return;
    setDraft(value == null ? "" : String(value));
    setError("");
    setEditing(true);
  }

  async function save() {
    const next = coerceForServer(type, draft);
    // No-op when value didn't change.
    const same = (next === value) || (next == null && (value == null || value === ""));
    if (same) { setEditing(false); return; }
    setSaving(true);
    setError("");
    try {
      await updateLease(locationId, leaseId, { [field]: next }, { before: lease });
      onSaved?.({ field, value: next });
      setEditing(false);
    } catch (err) {
      log.error("EditableLeaseField save failed", { field, err: String(err) });
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setEditing(false);
    setError("");
  }

  function onKey(e) {
    if (e.key === "Enter" && type !== "select") { e.preventDefault(); save(); }
    if (e.key === "Escape") { e.preventDefault(); cancel(); }
  }

  // ── Render mode ────────────────────────────────────────────────────────
  const rowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "9px 0",
    borderBottom: `1px solid ${T.border}`,
    cursor: canWrite && !editing ? "pointer" : "default",
  };

  const isEmpty = value == null || value === "";
  const displayValue = isEmpty
    ? (emptyLabel ? <span style={{ fontSize: T.fsBase, color: T.mid }}>{emptyLabel}</span> : <NotSpecified />)
    : (render ? render(value) : <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>{String(value)}</span>);

  if (!editing) {
    return (
      <div
        data-testid={`lease-field-${field}`}
        onClick={startEdit}
        style={rowStyle}
        title={canWrite ? "Click to edit" : undefined}
      >
        <span data-testid={`lease-field-label-${field}`} style={{ fontSize: T.fsBase, color: T.mid, fontWeight: T.fwRegular, flexShrink: 0 }}>{label}</span>
        <span data-testid={`lease-field-value-${field}`} style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0, textAlign: "right" }}>
          {displayValue}
          {!isEmpty && suffix && <span style={{ fontSize: T.fsSm, color: T.mid }}>{suffix}</span>}
          {dateBadge}
          {source && (
            <ProvenanceTooltip
              source={source}
              field={field}
              importedDate={source === "spacebase" ? lease?.lastCsvImportedAt : null}
              conflictingImport={conflictsMap?.[field] ?? null}
              onConflictKeepManual={
                conflictsMap?.[field]
                  ? () => onConflictResolve?.(conflictsMap[field].diffId, "keep_manual")
                  : undefined
              }
              onConflictUseIncoming={
                conflictsMap?.[field]
                  ? () => onConflictResolve?.(conflictsMap[field].diffId, "use_incoming")
                  : undefined
              }
            />
          )}
        </span>
      </div>
    );
  }

  // ── Edit mode ──────────────────────────────────────────────────────────
  const inputStyle = {
    fontSize: T.fsBase,
    padding: "4px 8px",
    border: `1px solid ${error ? T.red : T.borderDark}`,
    borderRadius: 4,
    width: "100%",
    fontFamily: "inherit",
    outline: "none",
  };

  const control = type === "select" ? (
    <select
      ref={inputRef}
      data-testid={`lease-field-input-${field}`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={onKey}
      style={inputStyle}
    >
      <option value=""> - Select - </option>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  ) : type === "date" ? (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <DateInput
        ref={inputRef}
        data-testid={`lease-field-input-${field}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        style={{ ...inputStyle, flex: 1 }}
        disabled={saving}
      />
      {draft && (
        <button
          type="button"
          data-testid={`lease-field-clear-${field}`}
          onClick={() => setDraft("")}
          title="Clear date"
          aria-label="Clear date"
          disabled={saving}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: T.mid,
            fontSize: T.fsMd,
            lineHeight: 1,
            padding: "4px 2px",
            flexShrink: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  ) : (
    <input
      ref={inputRef}
      type={type === "number" ? "number" : "text"}
      data-testid={`lease-field-input-${field}`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={onKey}
      style={inputStyle}
      disabled={saving}
    />
  );

  return (
    <div data-testid={`lease-field-${field}`} style={{ ...rowStyle, alignItems: "flex-start" }}>
      <span data-testid={`lease-field-label-${field}`} style={{ fontSize: T.fsBase, color: T.mid, fontWeight: T.fwRegular, flexShrink: 0, paddingTop: 6 }}>{label}</span>
      <div style={{ flex: 1, maxWidth: "60%", display: "flex", flexDirection: "column", gap: 4 }}>
        {control}
        {error && <div data-testid={`lease-field-error-${field}`} style={{ fontSize: T.fsXs, color: T.red }}>{error}</div>}
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.mid, padding: "3px 8px", borderRadius: 4, fontSize: T.fsXs, cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid={`lease-field-save-${field}`}
            style={{ background: T.blue, border: "none", color: T.white, padding: "3px 10px", borderRadius: 4, fontSize: T.fsXs, fontWeight: T.fwSemibold, cursor: "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
